import React, { useEffect, useMemo, useState } from 'react';
import useCalculatorService, { ExpenseMainCategory, CashFlowType } from './services/useCalculatorService'; // adjust path

const timeForDate = (yyyyMmDd) => new Date(`${yyyyMmDd}T22:59:59.999Z`);
const toInputDateUTC = (date) => {
    const d = new Date(date);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// For rendering the category dropdown
const CATEGORY_OPTIONS = [
    { label: 'Ավելացում', value: ExpenseMainCategory.Add },
    { label: 'Ժամանց', value: ExpenseMainCategory.Entertainment },
    { label: 'Տուն', value: ExpenseMainCategory.Home },
    { label: 'Այլ', value: ExpenseMainCategory.Other },
    { label: 'Օբոս', value: ExpenseMainCategory.Obos },
    { label: 'Շաբաթական', value: ExpenseMainCategory.Weekly },
];

export default function LimitsView() {
    const { getLimits, addLimit, updateLimit, getExpensesByLimit, addExpense, updateExpense } = useCalculatorService();

    const [newExpenseName, setNewExpenseName] = useState('');
    const [newExpenseAmount, setNewExpenseAmount] = useState('');
    const [newExpenseComment, setNewExpenseComment] = useState('');
    const [newExpenseType, setNewExpenseType] = useState(CashFlowType.Out);
    const [newExpenseDateFrom, setNewExpenseDateFrom] = useState(() => new Date());
    const [savingExpense, setSavingExpense] = useState(false);

    // for editing expense (client-side only unless you add backend endpoint)
    const [editingExpenseId, setEditingExpenseId] = useState(null);
    const [showAddLimit, setShowAddLimit] = useState(false);
    const [editingExpense, setEditingExpense] = useState({
        name: '',
        amount: '',
        comment: '',
        type: CashFlowType.Out,
        dateFrom: new Date(),
    });

    // user-selected filters / refresh params
    const [fromDate, setFromDate] = useState(() => new Date()); // used for GET /limit?fromDate=...

    // data
    const [limits, setLimits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // edit
    const [editingId, setEditingId] = useState(null);
    const [editingAmount, setEditingAmount] = useState('');

    // create
    const [newName, setNewName] = useState('');
    const [newAmount, setNewAmount] = useState('');
    const [newCategory, setNewCategory] = useState(ExpenseMainCategory.Entertainment);
    const [newDateFrom, setNewDateFrom] = useState(() => new Date());
    const [newDateTo, setNewDateTo] = useState(() => new Date());
    // optional: show expenses for a limit
    const [expandedLimitId, setExpandedLimitId] = useState(null);
    const [limitExpenses, setLimitExpenses] = useState([]);
    const [loadingExpenses, setLoadingExpenses] = useState(false);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getLimits({ fromDate });
            setLimits(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e?.message ?? 'Failed to load limits');
        } finally {
            setLoading(false);
        }
    };

    const reloadExpandedExpenses = async () => {
        if (!expandedLimitId) return;
        setLoadingExpenses(true);
        try {
            const data = await getExpensesByLimit(expandedLimitId);
            setLimitExpenses(Array.isArray(data) ? data : []);
        } finally {
            setLoadingExpenses(false);
        }
    };
    const createExpenseForExpandedLimit = async () => {
        setError('');
        try {
            if (!expandedLimitId) throw new Error('No limit selected');
            const amountNum = Number(newExpenseAmount);
            if (Number.isNaN(amountNum)) throw new Error('Expense amount must be a number');
            let label = "";
            if (!newExpenseName.trim()) {
                label = `${newExpenseType == 0 ? 'in' : 'Out'}: ${newExpenseAmount}`;
            } else {
                label = newExpenseName;
            }
            setSavingExpense(true);

            const expandedLimit = limits.find((x) => x.id === expandedLimitId);
            const expensePayload = {
                name: label.trim(),
                amount: amountNum,
                dateFrom: newExpenseDateFrom.toISOString(),
                category: expandedLimit?.category, // <-- instead of "category"
                type: newExpenseType,
                comment: newExpenseComment ?? '',
            };

            const res = await addExpense({ expense: expensePayload, limitId: expandedLimitId });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(text || 'Failed to add expense');
            }

            setNewExpenseName('');
            setNewExpenseAmount('');
            setNewExpenseComment('');
            setNewExpenseType(CashFlowType.Out);
            setNewExpenseDateFrom(new Date());

            await reloadExpandedExpenses();
            await load();
        } catch (e) {
            setError(e?.message ?? 'Failed to add expense');
        } finally {
            setSavingExpense(false);
        }
    };

    const startEditExpense = (exp) => {
        setEditingExpenseId(exp.id);
        setEditingExpense({
            name: exp.name ?? '',
            amount: String(exp.amount ?? ''),
            comment: exp.comment ?? '',
            type: exp.type ?? CashFlowType.Out,
            dateFrom: exp.dateFrom ? new Date(exp.dateFrom) : new Date(),
        });
    };

    const cancelEditExpense = () => {
        setEditingExpenseId(null);
        setEditingExpense({
            name: '',
            amount: '',
            comment: '',
            type: CashFlowType.Out,
            dateFrom: new Date(),
        });
    };
    const saveEditExpense = async () => {
        setError('');
        try {
            const amountNum = Number(editingExpense.amount);

            if (!editingExpense.name?.trim()) throw new Error('Expense name is required');
            if (Number.isNaN(amountNum)) throw new Error('Expense amount must be a number');
            if (!editingExpenseId) throw new Error('No expense selected');

            const expensePayload = {
                ...editingExpense,
                name: editingExpense.name.trim(),
                comment: editingExpense.comment ?? '',
                amount: amountNum,
                // if backend expects a string date, send ISO; if it expects DateTime, ISO is fine too
                dateFrom: editingExpense.dateFrom?.toISOString?.() ?? editingExpense.dateFrom,
            };

            const res = await updateExpense({
                id: editingExpenseId,
                expense: expensePayload,
            });

            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(text || 'Failed to update expense');
            }

            setLimitExpenses((prev) =>
                prev.map((x) =>
                    x.id === editingExpenseId
                        ? {
                            ...x,
                            ...expensePayload,
                            amount: amountNum,
                            dateFrom: expensePayload.dateFrom,
                        }
                        : x
                )
            );

            cancelEditExpense();
            await load();
        } catch (e) {
            setError(e?.message ?? 'Failed to edit expense');
        }
    };

    useEffect(() => {
        load();
    }, [fromDate]);

    const startEdit = (limit) => {
        setEditingId(limit.id);
        setEditingAmount(String(limit.amount ?? ''));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingAmount('');
    };

    const saveEdit = async (id) => {
        setError('');
        try {
            const amountNum = Number(editingAmount);
            if (Number.isNaN(amountNum)) throw new Error('Amount must be a number');

            const res = await updateLimit({ id, amount: amountNum });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(text || 'Update failed');
            }

            setLimits((prev) => prev.map((l) => (l.id === id ? { ...l, amount: amountNum } : l)));
            cancelEdit();
        } catch (e) {
            setError(e?.message ?? 'Failed to update limit');
        }
    };

    const createLimit = async () => {
        setError('');
        try {
            const amountNum = Number(newAmount);
            if (!newName.trim()) throw new Error('Անունը պարտադիր է');
            if (Number.isNaN(amountNum)) throw new Error('Amount must be a number');

            const payload = {
                name: newName.trim(),
                amount: amountNum,
                category: newCategory,
                dateFrom: newDateFrom.toISOString(), // UTC
                dateTo: newDateTo.toISOString(),     // UTC
            };

            const res = await addLimit(payload);
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(text || 'Create failed');
            }

            setNewName('');
            setNewAmount('');
            // keep category/dates as-is (or reset if you prefer)
            await load();
        } catch (e) {
            setError(e?.message ?? 'Failed to create limit');
        }
    };

    const toggleShowAddLimit = () => {
        setShowAddLimit(!showAddLimit);
    }

    const toggleExpenses = async (limitId) => {
        if (expandedLimitId === limitId) {
            setExpandedLimitId(null);
            setLimitExpenses([]);
            return;
        }

        setExpandedLimitId(limitId);
        setLoadingExpenses(true);
        setError('');
        try {
            const data = await getExpensesByLimit(limitId);
            setLimitExpenses(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e?.message ?? 'Failed to load limit expenses');
            setLimitExpenses([]);
        } finally {
            setLoadingExpenses(false);
        }
    };

    return (
        <div style={{ padding: 16, maxWidth: '95%' }}>
            <h2>Լիմիտներ</h2>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>

                <div>
                    <label>Ընտրել լիմիտների օրը</label>
                    <br />
                    <input
                        type="date"
                        value={toInputDateUTC(fromDate)}
                        onChange={(e) => setFromDate(timeForDate(e.target.value))}
                    />
                </div>

                <button onClick={load} disabled={loading}>
                    {loading ? 'Loading…' : 'Թարմացնել'}
                </button>
            </div>

            {error ? <p style={{ color: 'crimson', marginTop: 12 }}>{error}</p> : null}

            <button style={{ marginTop: 20 }} onClick={toggleShowAddLimit}>Ավելացնել լիմիտ</button>

            {showAddLimit &&
                (<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
                    <div>
                        <label>Անուն</label>
                        <br />
                        <input value={newName} onChange={(e) => setNewName(e.target.value)} />
                    </div>

                    <div>
                        <label>Քանակ</label>
                        <br />
                        <input
                            value={newAmount}
                            onChange={(e) => setNewAmount(e.target.value)}
                            inputMode="decimal"
                        />
                    </div>

                    <div>
                        <label>Կատեգորիա</label>
                        <br />
                        <select value={newCategory} onChange={(e) => setNewCategory(Number(e.target.value))}>
                            {CATEGORY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label>Ամսաթիվ սկսած</label>
                        <br />
                        <input
                            type="date"
                            value={toInputDateUTC(newDateFrom)}
                            onChange={(e) => setNewDateFrom(timeForDate(e.target.value))}
                        />
                    </div>

                    <div>
                        <label>Ամսաթիվ մինչ</label>
                        <br />
                        <input
                            type="date"
                            value={toInputDateUTC(newDateTo)}
                            onChange={(e) => setNewDateTo(timeForDate(e.target.value))}
                        />
                    </div>

                    <button onClick={createLimit}>Ստեղծել</button>
                </div>)}

            <h3 style={{ marginTop: 20 }}>Լիմիտներ</h3>

            <table width="100%" cellPadding="8" style={{ borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ textAlign: 'left' }}>
                        <th style={{ borderBottom: '1px solid #ddd' }}>Անուն</th>
                        <th style={{ borderBottom: '1px solid #ddd' }}>Քանակ</th>
                        <th style={{ borderBottom: '1px solid #ddd' }}>Մնացորդ</th>
                        <th style={{ borderBottom: '1px solid #ddd' }}>Ամսաթիվ սկսած</th>
                        <th style={{ borderBottom: '1px solid #ddd' }}>Ամսաթիվ մինչ</th>
                        <th style={{ borderBottom: '1px solid #ddd' }} />
                    </tr>
                </thead>

                <tbody>
                    {limits.map((l) => {
                        const isEditing = editingId === l.id;
                        const isExpanded = expandedLimitId === l.id;

                        return (
                            <React.Fragment key={l.id}>
                                <tr>
                                    <td style={{ borderBottom: '1px solid #f0f0f0' }}>{l.name}</td>

                                    <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        {isEditing ? (
                                            <input
                                                value={editingAmount}
                                                onChange={(e) => setEditingAmount(e.target.value)}
                                                inputMode="decimal"
                                                style={{ width: 120 }}
                                            />
                                        ) : (
                                            l.amount
                                        )}
                                    </td>

                                    <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        {l.amountLeft}
                                    </td>


                                    <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        {l.dateFrom ? new Date(l.dateFrom).toLocaleDateString() : ''}
                                    </td>

                                    <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        {l.dateTo ? new Date(l.dateTo).toLocaleDateString() : ''}
                                    </td>

                                    <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {isEditing ? (
                                                <>
                                                    <button onClick={() => saveEdit(l.id)}>Save</button>
                                                    <button onClick={cancelEdit}>Cancel</button>
                                                </>
                                            ) : (
                                                <button onClick={() => startEdit(l)}>Փոխել քանակը</button>
                                            )}

                                            <button onClick={() => toggleExpenses(l.id)}>
                                                {isExpanded ? 'թաքցնել ծախսերը' : 'Ցույց տալ ծախսերը'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {isExpanded ? (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}
                                        >
                                            <div style={{ padding: 8 }}>
                                                <strong>Ցույց տալ ծախսերը:</strong>

                                                {/* Add expense form */}
                                                <div
                                                    style={{
                                                        marginTop: 10,
                                                        display: 'flex',
                                                        gap: 8,
                                                        flexWrap: 'wrap',
                                                        alignItems: 'end',
                                                    }}
                                                >
                                                    <div>
                                                        <label>Անուն</label>
                                                        <br />
                                                        <input
                                                            value={newExpenseName}
                                                            onChange={(e) => setNewExpenseName(e.target.value)}
                                                            placeholder="e.g. Cinema"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label>Քանակ</label>
                                                        <br />
                                                        <input
                                                            value={newExpenseAmount}
                                                            onChange={(e) => setNewExpenseAmount(e.target.value)}
                                                            inputMode="decimal"
                                                            placeholder="e.g. 12.50"
                                                            style={{ width: 120 }}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label>Տիպ</label>
                                                        <br />
                                                        <select
                                                            value={newExpenseType}
                                                            onChange={(e) => setNewExpenseType(Number(e.target.value))}
                                                        >
                                                            <option value={CashFlowType.Out}>Out</option>
                                                            <option value={CashFlowType.In}>In</option>
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label>Ամսաթիվ</label>
                                                        <br />
                                                        <input
                                                            type="date"
                                                            value={toInputDateUTC(newExpenseDateFrom)}
                                                            onChange={(e) => setNewExpenseDateFrom(timeForDate(e.target.value))}
                                                        />
                                                    </div>

                                                    <button onClick={createExpenseForExpandedLimit} disabled={savingExpense}>
                                                        {savingExpense ? 'Adding…' : 'Add expense'}
                                                    </button>
                                                </div>

                                                {/* List */}
                                                {loadingExpenses ? (
                                                    <div style={{ marginTop: 10 }}>Սպասեք…</div>
                                                ) : limitExpenses.length ? (
                                                    <table
                                                        width="100%"
                                                        cellPadding="8"
                                                        style={{ borderCollapse: 'collapse', marginTop: 12 }}
                                                    >
                                                        <thead>
                                                            <tr style={{ textAlign: 'left' }}>
                                                                <th style={{ borderBottom: '1px solid #e5e5e5' }}>Անուն</th>
                                                                <th style={{ borderBottom: '1px solid #e5e5e5' }}>Քանակ</th>
                                                                <th style={{ borderBottom: '1px solid #e5e5e5' }}>ՏԻպ</th>
                                                                <th style={{ borderBottom: '1px solid #e5e5e5' }}>Օր</th>
                                                                <th style={{ borderBottom: '1px solid #e5e5e5' }} />
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {[...limitExpenses]
                                                                .sort((a, b) => new Date(b.dateFrom) - new Date(a.dateFrom)) // newest first
                                                                .map((e, idx) => {
                                                                    const isEditingExp = editingExpenseId === e.id;

                                                                    return (
                                                                        <tr key={e.id ?? idx}
                                                                            style={{
                                                                                backgroundColor:
                                                                                    e.type === CashFlowType.In
                                                                                        ? 'rgba(34, 197, 94, 0.06)'
                                                                                        : 'rgba(239, 68, 68, 0.06)' 
                                                                            }}>
                                                                            <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                                                {isEditingExp ? (
                                                                                    <input
                                                                                        value={editingExpense.name}
                                                                                        onChange={(ev) =>
                                                                                            setEditingExpense((s) => ({ ...s, name: ev.target.value }))
                                                                                        }
                                                                                    />
                                                                                ) : (
                                                                                    e.name
                                                                                )}
                                                                            </td>

                                                                            <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                                                {isEditingExp ? (
                                                                                    <input
                                                                                        value={editingExpense.amount}
                                                                                        onChange={(ev) =>
                                                                                            setEditingExpense((s) => ({ ...s, amount: ev.target.value }))
                                                                                        }
                                                                                        inputMode="decimal"
                                                                                        style={{ width: 110 }}
                                                                                    />
                                                                                ) : (
                                                                                    e.amount
                                                                                )}
                                                                            </td>

                                                                            <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                                                {isEditingExp ? (
                                                                                    <select
                                                                                        value={editingExpense.type}
                                                                                        onChange={(ev) =>
                                                                                            setEditingExpense((s) => ({
                                                                                                ...s,
                                                                                                type: Number(ev.target.value),
                                                                                            }))
                                                                                        }
                                                                                    >
                                                                                        <option value={CashFlowType.Out}>Out</option>
                                                                                        <option value={CashFlowType.In}>In</option>
                                                                                    </select>
                                                                                ) : e.type === CashFlowType.In ? (
                                                                                    'In'
                                                                                ) : (
                                                                                    'Out'
                                                                                )}
                                                                            </td>

                                                                            <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                                                {isEditingExp ? (
                                                                                    <input
                                                                                        type="date"
                                                                                        value={toInputDateUTC(editingExpense.dateFrom)}
                                                                                        onChange={(ev) =>
                                                                                            setEditingExpense((s) => ({
                                                                                                ...s,
                                                                                                dateFrom: timeForDate(ev.target.value),
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                ) : e.dateFrom ? (
                                                                                    new Date(e.dateFrom).toLocaleDateString()
                                                                                ) : (
                                                                                    ''
                                                                                )}
                                                                            </td>

                                                                            <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                                                {isEditingExp ? (
                                                                                    <input
                                                                                        value={editingExpense.comment}
                                                                                        onChange={(ev) =>
                                                                                            setEditingExpense((s) => ({ ...s, comment: ev.target.value }))
                                                                                        }
                                                                                    />
                                                                                ) : (
                                                                                    e.comment ?? ''
                                                                                )}
                                                                            </td>

                                                                            <td style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                                                {isEditingExp ? (
                                                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                                                        <button onClick={saveEditExpense}>Save</button>
                                                                                        <button onClick={cancelEditExpense}>Cancel</button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <button onClick={() => startEditExpense(e)}>Փոխել</button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div style={{ marginTop: 10, color: '#666' }}>Չկան.</div>
                                                )}

                                                <div style={{ marginTop: 8, color: '#666' }}>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })}

                    {!limits.length && !loading ? (
                        <tr>
                            <td colSpan={5} style={{ padding: 16, color: '#666' }}>
                                Տվյալ կատեգորիայի համար լիմիտներ չկան
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </table>
        </div>
    );
}